import { useCallback, useMemo, useRef } from 'react'
import { ApolloError } from '@apollo/client'
import conf from '~/app/conf'
import { getYaNative } from '@yanolja-rnd/ya-native'
import { isNative } from '~/app/utils/typeguards'
import { IAxiosClientError } from '~/app/react-query/axios'

export type CustomError = {
  current: ApolloError
}

export const getIsForbiddenError = (e?: ApolloError) => {
  if (!e) {
    return false
  }
  return e.graphQLErrors.some(e => {
    const response = e.extensions?.response as { status?: number }
    return response?.status === 403
  })
}

export const getStatus = e => e.extensions?.response?.status

export const getIsNotAuthorized = e => {
  if (!e) {
    return false
  }
  const { graphQLErrors } = e
  const notAuthorizedCodes = ['UNAUTHENTICATED', 'USER_LOGIN', 'VISITOR_LOGIN']
  return (
    graphQLErrors.filter(e => getStatus(e) === 401).length > 0 ||
    graphQLErrors.filter(e => notAuthorizedCodes.includes(e.extensions?.code)).length > 0
  )
}

export const goToDomesticAuthPage = e => {
  const { graphQLErrors } = e
  if (graphQLErrors.some(e => e.extensions?.code === 'VISITOR_LOGIN')) {
    window.location.replace(`${conf.web.url}/visitorlogin?type=platformOrder`)
    return
  }
  login()
}

const login = () => {
  const yaNative = getYaNative()
  if (isNative(yaNative)) {
    yaNative.login()
  } else {
    window.location.replace(
      `${conf.web.url}/login?redirect=${encodeURIComponent(
        `${window.location.origin}${window.location.pathname}${window.location.search}`
      )}`
    )
  }
}

const useErrorHandler = () => {
  /**
   * query hook 을 사용하지 않고 직접 query 를 callback 호출하게 되면 error 를 전달받기가 어려워진다.
   * 이런경우에 좀더 유용하게 쓸수 있도록 error 를 ref 로 제공하여 접근할 수 있도록 해준다.
   * useState 를 사용할 경우 렌더링이 다시 일어나 query 를 다시 트리거 하여 무한 반복에 빠지는 경우가 있으니 주의 할 것
   */
  const error = useRef<ApolloError | null>()
  const errorHandler = useCallback(
    (e?: ApolloError | CustomError) => {
      if (!e) {
        error.current = null
        return
      }

      const apolloError = 'current' in e ? e.current : e
      const isNotAuthorized = getIsNotAuthorized(apolloError)
      if (isNotAuthorized) {
        goToDomesticAuthPage(e)
        return
      }
      if (error.current !== apolloError) {
        error.current = apolloError
      }
    },
    [error]
  )

  return {
    errorHandler,
    error,
  }
}

export const useErrorRetryParams = (error?: ApolloError) => {
  return useMemo(() => {
    const isForbiddenError = getIsForbiddenError(error)
    const isNotAuthorized = getIsNotAuthorized(error)
    /**
     * title, subTitle 403 이외의 status 는 기본 값 사용
     */
    return {
      title: isForbiddenError ? '회원정보가 일치하지 않습니다.' : undefined,
      subTitle: isForbiddenError ? '홈으로 이동 후 다시 접속해 주세요.' : undefined,
      hasError: !!error,
      hasFailOverButton: !isForbiddenError,
      hasBackButton: !isForbiddenError,
      hasHomeButton: isForbiddenError,
      isNotAuthorized,
    }
  }, [error])
}

const getGlobalIsNotAuthorized = (e: IAxiosClientError | null) => {
  if (!e) return false

  const { status, code } = e.response?.data || {}
  return status === 401 || code === 'UNAUTHENTICATED'
}

/**
 * GlobalRetry Component Props
 */
export const useErrorGlobalRetryParams = (error: IAxiosClientError | null) => {
  return useMemo(() => {
    const isForbiddenError = error?.response?.status === 403
    const isNotAuthorized = getGlobalIsNotAuthorized(error)

    /**
     * title, subTitle 403 이외의 status 는 기본 값 사용
     */
    return {
      title: isForbiddenError ? '회원정보가 일치하지 않습니다.' : undefined,
      subTitle: isForbiddenError ? '홈으로 이동 후 다시 접속해 주세요.' : undefined,
      hasFailOverButton: !isForbiddenError,
      hasBackButton: !isForbiddenError,
      hasHomeButton: isForbiddenError,
      hasError: !!error,
      isNotAuthorized,
    }
  }, [error])
}

export default useErrorHandler
